import React, { createContext, useState, useContext } from 'react';
import { useCookies } from 'react-cookie';
import packageInfo from '../../package.json';

interface User {
  id: string;
  name: string;
  email: string;
  accessToken: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  CurrentVersionUpdated: boolean;
  login: (userData: User) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cookies, setCookie, removeCookie] = useCookies(['usuario', 'currentVersion']);

  const initialUser = (() => {
    if (cookies.usuario) {
      if (typeof cookies.usuario === 'string') {
        try {
          return JSON.parse(cookies.usuario) as User;
        } catch (error) {
          console.error('Erro ao obter usuário dos cookies:', error);
          removeCookie('usuario');
        }
      } else {
        return cookies.usuario as User;
      }
    }
    return null;
  })();

  const [user, setUser] = useState<User | null>(initialUser);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(!!initialUser);

  const CurrentVersionUpdated = packageInfo.version === cookies.currentVersion || false;

  const login = (userData: User) => {
    setUser(userData);
    setIsAuthenticated(true);
    setCookie('usuario', JSON.stringify(userData), {
      path: '/',
    });
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    removeCookie('usuario', { path: '/' });
  };

  return <AuthContext.Provider value={{ isAuthenticated, user, login, logout, CurrentVersionUpdated }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
